import {
  LearningOpportunityStatus,
  LearningOpportunityType,
  StudyFormat,
} from "@/server/backend-api/generated";
import { addOrRemove as _addOrRemove } from "@/utils/array";
import {
  getLevelStringFromId,
  loTypeToString,
  statusToString,
  studyFormatToString,
} from "@/server/backend-api/enumToText";

export interface LearningOpportunityFilterState {
  selectedLearningOppTypes: LearningOpportunityType[];
  selectedTopicIds: string[];
  selectedUniversityIds: string[];
  selectedLearningOppStatuses: LearningOpportunityStatus[];
  selectedStudyFormats: StudyFormat[];
  selectedTagIds: string[];
  selectedLevelIds: string[];
  selectedPaces: string[];
  selectedCredits: string[];
  selectedStudyPeriodsFrom: string[];
  selectedStudyPeriodsTo: string[];
  selectedAudienceIds: string[];
}

interface WithName<T> {
  value: T;
  name: string;
}

export interface LearningOpportunityFilterDisplay {
  selectedLearningOppTypes: WithName<LearningOpportunityType>[];
  selectedTopicIds: WithName<string>[];
  selectedUniversityIds: WithName<string>[];
  selectedLearningOppStatuses: WithName<LearningOpportunityStatus>[];
  selectedStudyFormats: WithName<StudyFormat>[];
  selectedTagIds: WithName<string>[];
  selectedLevelIds: WithName<string>[];
  selectedPaces: WithName<string>[];
  selectedCredits: WithName<string>[];
  selectedStudyPeriodsFrom: WithName<string>[];
  selectedStudyPeriodsTo: WithName<string>[];
  selectedAudienceIds: WithName<string>[];
}

function _deepCopyFilter(
  filter: LearningOpportunityFilterState
): LearningOpportunityFilterState {
  return {
    selectedLearningOppTypes: [...filter.selectedLearningOppTypes],
    selectedTopicIds: [...filter.selectedTopicIds],
    selectedUniversityIds: [...filter.selectedUniversityIds],
    selectedLearningOppStatuses: [...filter.selectedLearningOppStatuses],
    selectedStudyFormats: [...filter.selectedStudyFormats],
    selectedTagIds: [...filter.selectedTagIds],
    selectedLevelIds: [...filter.selectedLevelIds],
    selectedPaces: [...filter.selectedPaces],
    selectedCredits: [...filter.selectedCredits],
    selectedStudyPeriodsFrom: [...filter.selectedStudyPeriodsFrom],
    selectedStudyPeriodsTo: [...filter.selectedStudyPeriodsTo],
    selectedAudienceIds: [...filter.selectedAudienceIds],
  };
}

export function addOrRemoveLearningOppType(
  filter: LearningOpportunityFilterState,
  type: LearningOpportunityType,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedLearningOppTypes = _addOrRemove(
    newFilter.selectedLearningOppTypes,
    type,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveTopicId(
  filter: LearningOpportunityFilterState,
  competenceId: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedTopicIds = _addOrRemove(
    newFilter.selectedTopicIds,
    competenceId,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveAudienceId(
  filter: LearningOpportunityFilterState,
  audienceId: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedAudienceIds = _addOrRemove(
    newFilter.selectedAudienceIds,
    audienceId,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveTagId(
  filter: LearningOpportunityFilterState,
  tagId: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedTagIds = _addOrRemove(
    newFilter.selectedTagIds,
    tagId,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveLevelId(
  filter: LearningOpportunityFilterState,
  levelId: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedLevelIds = _addOrRemove(
    newFilter.selectedLevelIds,
    levelId,
    addOrRemove
  );
  return newFilter;
}
export function addOrRemoveUniversityId(
  filter: LearningOpportunityFilterState,
  universityId: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedUniversityIds = _addOrRemove(
    newFilter.selectedUniversityIds,
    universityId,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveLearningOppStatus(
  filter: LearningOpportunityFilterState,
  status: LearningOpportunityStatus,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedLearningOppStatuses = _addOrRemove(
    newFilter.selectedLearningOppStatuses,
    status,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveStudyFormat(
  filter: LearningOpportunityFilterState,
  format: StudyFormat,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedStudyFormats = _addOrRemove(
    newFilter.selectedStudyFormats,
    format,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemovePace(
  filter: LearningOpportunityFilterState,
  pace: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedPaces = _addOrRemove(
    newFilter.selectedPaces,
    pace,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveCredit(
  filter: LearningOpportunityFilterState,
  credit: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedCredits = _addOrRemove(
    newFilter.selectedCredits,
    credit,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveStudyPeriodFrom(
  filter: LearningOpportunityFilterState,
  studyPeriod: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedStudyPeriodsFrom = _addOrRemove(
    newFilter.selectedStudyPeriodsFrom,
    studyPeriod,
    addOrRemove
  );
  return newFilter;
}

export function addOrRemoveStudyPeriodTo(
  filter: LearningOpportunityFilterState,
  studyPeriod: string,
  addOrRemove: boolean
): LearningOpportunityFilterState {
  const newFilter = _deepCopyFilter(filter);
  newFilter.selectedStudyPeriodsTo = _addOrRemove(
    newFilter.selectedStudyPeriodsTo,
    studyPeriod,
    addOrRemove
  );
  return newFilter;
}

export function getLearningOppFilterDisplay(
  filter: LearningOpportunityFilterState,
  topicIdToName: Map<string, string>,
  universityIdToName: Map<string, string>,
  tagIdToName: Map<string, string>
): LearningOpportunityFilterDisplay {
  return {
    selectedLearningOppTypes: filter.selectedLearningOppTypes.map((type) => ({
      value: type,
      name: loTypeToString(type),
    })),
    selectedAudienceIds: filter.selectedAudienceIds.map((audienceId) => ({
      value: audienceId,
      name: audienceId,
    })),
    selectedTopicIds: filter.selectedTopicIds.map((topicId) => ({
      value: topicId,
      name: topicIdToName.get(topicId) ?? topicId,
    })),
    selectedTagIds: filter.selectedTagIds.map((tagId) => ({
      value: tagId,
      name: tagIdToName.get(tagId) ?? tagId,
    })),
    selectedLearningOppStatuses: filter.selectedLearningOppStatuses.map(
      (status) => ({
        value: status,
        name: statusToString(status),
      })
    ),
    selectedStudyFormats: filter.selectedStudyFormats.map((format) => ({
      value: format,
      name: studyFormatToString(format),
    })),
    selectedUniversityIds: filter.selectedUniversityIds.map((universityId) => ({
      value: universityId,
      name: universityIdToName.get(universityId) ?? universityId,
    })),
    selectedLevelIds: filter.selectedLevelIds.map((levelId) => ({
      value: levelId,
      name: getLevelStringFromId(levelId),
    })),
    selectedCredits: filter.selectedCredits.map((credit) => ({
      value: credit,
      name: credit,
    })),
    selectedPaces: filter.selectedPaces.map((pace) => ({
      value: pace,
      name: pace,
    })),
    selectedStudyPeriodsFrom: filter.selectedStudyPeriodsFrom.map(
      (studyPeriod) => ({
        value: studyPeriod,
        name: studyPeriod,
      })
    ),
    selectedStudyPeriodsTo: filter.selectedStudyPeriodsTo.map(
      (studyPeriod) => ({
        value: studyPeriod,
        name: studyPeriod,
      })
    ),
  };
}
